import React, { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Eye, FileDown, Loader2, Info, Tags, Camera } from "lucide-react";
import { reportTemplatesApi, doorsApi, inspectionsApi, quotesApi, companiesApi } from "@/services/api";
import { generateBuildingReport, generateDoorReport, downloadQuotePdf } from "@/pdf/webReports";
import { useAsync } from "@/hooks/useAsync";
import { apiError } from "@/lib/format";
import { useAuth, userHasRole } from "@/context/AuthContext";
import { SECTION_DEFS, EMPTY_TEMPLATE } from "@/lib/reportSections";
import PageHeader from "@/components/PageHeader";
import { LoadingState } from "@/components/DataStates";
import ReportDoorPicker, { buildDoorPicks } from "@/components/reports/ReportDoorPicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function ReportBuilder() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.is_company_admin || userHasRole(user, "admin");

  const modeParam = sp.get("mode");
  const mode = modeParam === "door" ? "door" : modeParam === "quote" ? "quote" : "building";
  const buildingId = sp.get("buildingId") || "";
  const doorId = sp.get("doorId") || "";
  const quoteId = sp.get("quoteId") || "";
  const isQuote = mode === "quote";
  const reportType = isQuote ? "quote" : "inspection";

  const [tpl, setTpl] = useState(EMPTY_TEMPLATE);
  const [saveAsDefault, setSaveAsDefault] = useState({});
  const [includeCompliance, setIncludeCompliance] = useState(true);
  const [selectedDoorIds, setSelectedDoorIds] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Quote-mode pricing presentation (mirrors mobile reports/edit.tsx).
  const [displayMode, setDisplayMode] = useState("labour_materials");
  const [displaySource, setDisplaySource] = useState("company");
  const [displayTouched, setDisplayTouched] = useState(false);
  const [showDoorPhotos, setShowDoorPhotos] = useState(true);
  const [photosTouched, setPhotosTouched] = useState(false);
  const [photoAppendix, setPhotoAppendix] = useState(false);
  const [appendixTouched, setAppendixTouched] = useState(false);

  // Pre-fill editable sections from the company's saved template (per report type),
  // and — in quote mode — resolve the pricing/photo defaults from the quote & company.
  const { loading } = useAsync(async () => {
    try {
      const res = await reportTemplatesApi.get(reportType);
      const next = { ...EMPTY_TEMPLATE };
      for (const def of SECTION_DEFS) next[def.key] = res.data?.[def.key] ?? "";
      setTpl(next);
    } catch (e) {
      console.warn("Failed to load report template defaults", e);
    }
    if (isQuote && quoteId) {
      try {
        const [quoteR, mc] = await Promise.all([
          quotesApi.getOne(quoteId),
          companiesApi.me().then((r) => r.data).catch(() => ({})),
        ]);
        const quote = quoteR.data || {};
        const pick = (v) => (v === "labour_materials" || v === "total_only" ? v : null);
        const fromQuote = pick(quote.display_mode);
        const fromCompany = pick(mc?.quote_display_mode);
        const isInspectionQuote = (quote.quote_type || "maintenance") === "inspection";
        setDisplayMode(fromQuote || fromCompany || (isInspectionQuote ? "total_only" : "labour_materials"));
        setDisplaySource(fromQuote ? "quote" : "company");
        setShowDoorPhotos(typeof quote.show_door_photos === "boolean" ? quote.show_door_photos : mc?.quote_show_door_photos !== false);
        setPhotoAppendix(typeof quote.photo_appendix === "boolean" ? quote.photo_appendix : mc?.quote_photo_appendix === true);
        setIncludeCompliance(mc?.quote_include_compliance !== false);
      } catch (e) {
        console.warn("Failed to resolve quote display defaults", e);
      }
    }
    return true;
  }, [reportType, isQuote, quoteId]);

  // Building mode: load doors + inspections so the picker can show statuses.
  const doorData = useAsync(async () => {
    if (mode !== "building" || !buildingId) return [];
    const [doorsR, inspR] = await Promise.all([
      doorsApi.getAll(buildingId, { includePhotos: false }),
      inspectionsApi.getAll({ building_id: buildingId, include_photos: false, _fresh: true }),
    ]);
    return buildDoorPicks(doorsR.data || [], inspR.data || []);
  }, [mode, buildingId]);
  const doorPicks = Array.isArray(doorData.data) ? doorData.data : [];

  const handleChange = (key, val) => setTpl((prev) => ({ ...prev, [key]: val }));
  const toggleSave = (key) => {
    if (!isAdmin) { toast.error("Only company admins can update the saved defaults."); return; }
    setSaveAsDefault((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const persistDefaultsIfRequested = useCallback(async () => {
    const anyToggled = Object.values(saveAsDefault).some(Boolean);
    if (!anyToggled || !isAdmin) return;
    try {
      const current = await reportTemplatesApi.get(reportType);
      const merged = { ...EMPTY_TEMPLATE };
      for (const def of SECTION_DEFS) {
        merged[def.key] = saveAsDefault[def.key] ? tpl[def.key] : (current.data?.[def.key] ?? "");
      }
      await reportTemplatesApi.update(merged, reportType);
      toast.success("Saved as default for future reports");
    } catch (e) {
      toast.error(apiError(e, "PDF is fine, but defaults could not be saved."));
    }
  }, [saveAsDefault, isAdmin, tpl, reportType]);

  const persistQuoteDisplayChoice = useCallback(async () => {
    if (!isQuote || (!displayTouched && !photosTouched && !appendixTouched)) return;
    try {
      const patch = {};
      if (displayTouched) patch.display_mode = displayMode;
      if (photosTouched) patch.show_door_photos = showDoorPhotos;
      if (appendixTouched) patch.photo_appendix = photoAppendix;
      await quotesApi.update(quoteId, patch);
    } catch (e) {
      console.warn("Could not save the quote pricing display choice", e);
    }
  }, [isQuote, displayTouched, photosTouched, appendixTouched, displayMode, showDoorPhotos, photoAppendix, quoteId]);

  const build = async (action) => {
    if (action === "preview") setPreviewing(true); else setGenerating(true);
    try {
      if (action === "generate") await persistDefaultsIfRequested();
      if (isQuote) {
        if (!quoteId) throw new Error("Missing quote");
        if (action === "generate") await persistQuoteDisplayChoice();
        await downloadQuotePdf(quoteId, {
          customSections: tpl,
          includeCompliance,
          displayMode,
          showDoorPhotos,
          photoAppendix,
          download: action === "generate",
        });
      } else if (mode === "building") {
        if (!buildingId) throw new Error("Missing building");
        await generateBuildingReport(buildingId, {
          customSections: tpl,
          includeCompliance,
          download: action === "generate",
          persist: action === "generate",
          selectedDoorIds,
        });
      } else {
        if (!doorId || !buildingId) throw new Error("Missing door/building");
        await generateDoorReport(doorId, buildingId, {
          customSections: tpl,
          includeCompliance,
          download: action === "generate",
          persist: action === "generate",
        });
      }
      // On a free trial the backend returns a view-only preview (no PDF, no
      // save). webReports already surfaces the accurate "view-only" toast, so
      // suppress the generic success message to avoid a misleading green toast.
      let wasViewOnly = false;
      try { wasViewOnly = !!globalThis.__fdcheck_last_view_only; delete globalThis.__fdcheck_last_view_only; } catch { /* ignore */ }
      if (!wasViewOnly) {
        toast.success(action === "preview" ? "Preview opened in a new tab" : "Report generated — opened in a new tab & saved to Reports");
      }
    } catch (e) {
      toast.error(apiError(e, "Could not generate report"));
    } finally {
      setPreviewing(false);
      setGenerating(false);
    }
  };

  if (loading) return <div><LoadingState label="Loading report builder…" /></div>;

  const subtitle = isQuote
    ? "Quote — edit before sending"
    : mode === "building"
      ? "Building inspection report — edit before sending"
      : "Door inspection report — edit before sending";

  return (
    <div className="pb-4">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="report-builder-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <PageHeader title="Report builder" subtitle={subtitle} testId="report-builder-header" />

      <div className="max-w-3xl space-y-4">
        <div className="flex items-start gap-2 rounded-xl bg-sky-50 p-3 text-sm text-sky-900">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Sections are pre-filled from your saved defaults. Edit any block for this {isQuote ? "quote" : "report"}, or toggle “Save as default” to remember it for future {isQuote ? "quotes" : "reports"}.</p>
        </div>

        {/* Compliance & Golden Thread */}
        <Card className="border-red-200 bg-red-50/40 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-primary"><ShieldCheck className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900">Include Compliance &amp; Golden Thread section</p>
              <p className="mt-0.5 text-xs text-slate-500">Adds a cover-page blurb and appendix explaining how this {isQuote ? "quote" : "report"} supports the fire-door duties under FSER 2022, the RRFSO, the Fire Safety Act 2021 and the Building Safety Act’s Golden Thread.</p>
            </div>
            <Switch checked={includeCompliance} onCheckedChange={setIncludeCompliance} data-testid="toggle-compliance-section" />
          </div>
        </Card>

        {/* Door selection (building only) */}
        {mode === "building" && (
          doorData.loading ? (
            <Card className="p-4"><LoadingState label="Loading doors…" /></Card>
          ) : doorPicks.length > 0 ? (
            <ReportDoorPicker picks={doorPicks} value={selectedDoorIds} onChange={setSelectedDoorIds} />
          ) : null
        )}

        {/* Pricing detail chooser (quote only) */}
        {isQuote && (
          <Card className="p-4" data-testid="quote-pricing-card">
            <div className="mb-2 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-primary"><Tags className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900">Pricing detail</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {displaySource === "quote" ? "Set on this quote." : "Using your company default (Settings → Quote Template)."}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {[
                { key: "labour_materials", label: "Labour & materials separately", hint: "Each door shows a labour column, a materials column and the door total." },
                { key: "total_only", label: "One total per door", hint: "Each door shows just the jobs to be done and a single door total." },
              ].map((opt) => {
                const on = displayMode === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => { setDisplayMode(opt.key); setDisplayTouched(true); setDisplaySource("quote"); }}
                    data-testid={`pricing-mode-${opt.key}`}
                    className={`flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${on ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${on ? "border-primary bg-primary" : "border-slate-300"}`}>{on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}</span>
                    <span className="min-w-0">
                      <span className={`block text-sm font-semibold ${on ? "text-slate-900" : "text-slate-700"}`}>{opt.label}</span>
                      <span className="mt-0.5 block text-xs text-slate-500">{opt.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* Door photos + appendix (quote only) */}
        {isQuote && (
          <Card className="p-4" data-testid="quote-photos-card">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-primary"><Camera className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900">Show the failure photo on each door</p>
                <p className="mt-0.5 text-xs text-slate-500">Puts the photo the inspector took beside that door’s list of works. Turning this off makes the PDF smaller.</p>
              </div>
              <Switch checked={showDoorPhotos} onCheckedChange={(v) => { setShowDoorPhotos(v); setPhotosTouched(true); }} data-testid="toggle-door-photos" />
            </div>
            <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
              <Switch checked={photoAppendix} onCheckedChange={(v) => { setPhotoAppendix(v); setAppendixTouched(true); }} data-testid="toggle-photo-appendix" />
              <span className="text-xs text-slate-600">Also add a photo appendix at the back (same photos, printed large)</span>
            </div>
          </Card>
        )}

        {/* Editable sections */}
        {SECTION_DEFS.map((def) => (
          <Card key={def.key} className="p-4">
            <Label className="font-semibold text-slate-800">{def.label}</Label>
            <p className="mb-2 mt-0.5 text-xs text-slate-500">{def.hint}</p>
            <Textarea
              rows={4}
              value={tpl[def.key]}
              onChange={(e) => handleChange(def.key, e.target.value)}
              placeholder={def.placeholder}
              data-testid={`edit-${def.key}`}
            />
            <div className="mt-2 flex items-center gap-2">
              <Switch
                checked={!!saveAsDefault[def.key]}
                onCheckedChange={() => toggleSave(def.key)}
                disabled={!isAdmin}
                data-testid={`save-toggle-${def.key}`}
              />
              <span className={`text-xs ${isAdmin ? "text-slate-600" : "text-slate-400"}`}>Save as default for future {isQuote ? "quote" : "inspection"} reports</span>
            </div>
          </Card>
        ))}
      </div>

      <div className="sticky bottom-0 mt-6 flex max-w-3xl gap-3 border-t border-slate-200 bg-white/90 py-3 backdrop-blur">
        <Button variant="outline" className="flex-1" onClick={() => build("preview")} disabled={previewing || generating} data-testid="preview-report">
          {previewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Preview
        </Button>
        <Button className="flex-[1.4]" onClick={() => build("generate")} disabled={generating || previewing} data-testid="generate-report">
          {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />} {isQuote ? "Generate & save PDF" : "Generate & save PDF"}
        </Button>
      </div>
    </div>
  );
}
